import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

// ============================================
// DISCOUNT CODES
// ============================================

export interface DiscountCode {
  id: string;
  code: string;
  type: "percentage" | "fixed";
  value: number;
  max_uses: number | null;
  // Per-member redemption cap (null = unlimited). Enforced server-side
  // when validateDiscountCode is called with a member_email; counted
  // via the discount_code_redemptions ledger written by
  // markDiscountRedeemed.
  max_uses_per_member?: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  applicable_to: Record<string, any>;
  active: boolean;
  created_at: string;
}

export const getDiscountCodes = async (token: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/analytics/discount-codes`, {
      headers: authHeaders(token),
    });
    return response.data;
  } catch (error: any) {
    return { success: false, data: [], message: error?.message };
  }
};

export const createDiscountCode = async (
  token: string,
  data: Partial<DiscountCode>
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/analytics/discount-codes`,
      data,
      { headers: authHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || error?.message };
  }
};

export const updateDiscountCode = async (
  token: string,
  id: string,
  data: Partial<DiscountCode>
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/analytics/discount-codes/${id}`,
      data,
      { headers: authHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || error?.message };
  }
};

export const deleteDiscountCode = async (token: string, id: string) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/analytics/discount-codes/${id}`,
      { headers: authHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || error?.message };
  }
};

// ============================================
// CAMPAIGNS
// ============================================

export interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  audience_filter: Record<string, any>;
  start_date: string | null;
  end_date: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export const getCampaigns = async (token: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/analytics/campaigns`, {
      headers: authHeaders(token),
    });
    return response.data;
  } catch (error: any) {
    return { success: false, data: [], message: error?.message };
  }
};

export const createCampaign = async (
  token: string,
  data: Partial<Campaign>
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/analytics/campaigns`,
      data,
      { headers: authHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || error?.message };
  }
};

export const updateCampaign = async (
  token: string,
  id: string,
  data: Partial<Campaign>
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/analytics/campaigns/${id}`,
      data,
      { headers: authHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || error?.message };
  }
};

export const deleteCampaign = async (token: string, id: string) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/analytics/campaigns/${id}`,
      { headers: authHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.message || error?.message };
  }
};
